import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { useQuery, getGlobalConfig } from "wasp/client/operations";
import useLocalStorage from "../hooks/useLocalStorage";
import {
  type ViewModeId,
  type PortalPackDef,
  resolvePortalPack,
  isViewModeAllowed,
} from "../../gedoba/view-modes/viewModePacks";

export type ViewMode = ViewModeId;

const VIEW_MODE_STORAGE_KEY = "gedoba-view-mode";

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  portalPack: PortalPackDef;
  isMode: (mode: ViewModeId) => boolean;
  isHome: boolean;
  /** @deprecated prefer isMode("student_life") */
  isStudentLife: boolean;
  isAcademics: boolean;
  isCampus: boolean;
  isMarketplace: boolean;
  isCommunity: boolean;
  isBusiness: boolean;
  isManagement: boolean;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(
  undefined,
);

interface ViewModeProviderProps {
  children: ReactNode;
}

export function ViewModeProvider({ children }: ViewModeProviderProps) {
  const { data: globalConfig } = useQuery(getGlobalConfig);
  const portalPack = useMemo(
    () => resolvePortalPack((globalConfig as any)?.vertical),
    [(globalConfig as any)?.vertical],
  );

  const [viewMode, setStored] = useLocalStorage<ViewMode>(
    VIEW_MODE_STORAGE_KEY,
    "home",
  );

  // Prefer a real pack tab over "home" so module groups (incl. Building
  // management) are visible without an extra click after login / pack switch.
  useEffect(() => {
    const first = portalPack.modes[0]?.id;
    if (!first) return;
    if (viewMode === "home" || !isViewModeAllowed(portalPack, viewMode)) {
      setStored(first);
    }
  }, [portalPack, viewMode, setStored]);

  const value: ViewModeContextType = useMemo(
    () => ({
      viewMode,
      setViewMode: setStored,
      portalPack,
      isMode: (mode: ViewModeId) => viewMode === mode,
      isHome: viewMode === "home",
      isStudentLife: viewMode === "student_life",
      isAcademics: viewMode === "academics",
      isCampus: viewMode === "campus",
      isMarketplace: viewMode === "marketplace",
      isCommunity: viewMode === "community",
      isBusiness: viewMode === "business",
      isManagement: viewMode === "management",
    }),
    [viewMode, setStored, portalPack],
  );

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode(): ViewModeContextType {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error("useViewMode must be used within a ViewModeProvider");
  }
  return context;
}
